import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '../../components/Common/Box';
import TradingFeedback from './TradingFeedback';
import NiaPortfolioHistory from './NiaPortfolioHistory';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

const styles: Record<string, React.CSSProperties> = {
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 15, marginBottom: 30 },
    statCard: { textAlign: 'center', padding: 20, borderRadius: 12 },
    statLabel: { fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' },
    statValue: { fontSize: 20, fontWeight: 'bold' },
    primaryBtn: { backgroundColor: '#7c3aed', color: 'white', padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: '600', transition: 'all 0.2s' },
    sectionTitle: { fontSize: 18, fontWeight: '700', color: 'var(--text-heading)', marginBottom: 20, marginTop: 10 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '12px 10px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)' },
    thCenter: { textAlign: 'center', padding: '12px 10px', borderBottom: '2px solid var(--border-color)', fontWeight: 600, color: 'var(--text-muted)' },
    td: { padding: '12px 10px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'middle' },
    tdCenter: { padding: '12px 10px', borderBottom: '1px solid var(--border-color)', textAlign: 'center' },
    decisionCard: { backgroundColor: 'var(--bg-box)', padding: 20, borderRadius: 12, border: '1px solid var(--border-color)', marginBottom: 12 },
    decisionHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' },
    decisionTime: { display: 'flex', alignItems: 'center', gap: 10 },
    timeBadge: { fontSize: 11, backgroundColor: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '3px 10px', borderRadius: 6, fontWeight: 600 },
    decisionId: { fontSize: 11, color: 'var(--text-muted)' },
    decisionText: { margin: 0, whiteSpace: 'pre-wrap', fontSize: 14, color: 'var(--text-main)', lineHeight: '1.6' },
    readMoreBtn: { background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: 13, padding: 0, marginTop: 10, textDecoration: 'underline', fontWeight: 600 },
    actionsPanel: { marginTop: 20, padding: 15, backgroundColor: 'var(--bg-input)', borderRadius: 10, border: '1px solid var(--border-color)' },
    actionsTitle: { fontSize: 12, fontWeight: 'bold', color: '#0ea5e9', marginBottom: 8 },
    actionsContent: { margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', color: 'var(--text-main)' },
    adminConsole: { padding: 20, backgroundColor: 'var(--bg-box)', borderRadius: 12, border: '1px solid rgba(239, 68, 68, 0.3)', marginBottom: 30 },
    adminBtn: { padding: '10px 18px', borderRadius: 8, border: 'none', color: 'white', fontWeight: 'bold', fontSize: 13, cursor: 'pointer' },
    paywallContainer: { position: 'relative', width: '100%' },
};

const DecisionCard = ({ decision }: { decision: any }) => {
    const [expanded, setExpanded] = useState(false);
    const text = decision.ai_reasoning || '';
    const hasActions = !!decision.proposed_actions;
    const isLong = text.length > 300;
    const canExpand = isLong || hasActions || !!decision.execution_result;

    const decisionDate = new Date(decision.created_at || decision.date);
    const timeStr = decisionDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    let actions: any[] = [];
    try {
        if (decision.proposed_actions) {
            actions = typeof decision.proposed_actions === 'string'
                ? JSON.parse(decision.proposed_actions)
                : decision.proposed_actions;
        }
    } catch (e) { console.error("Error parsing actions", e); }

    return (
        <div style={styles.decisionCard}>
            <div style={styles.decisionHeader}>
                <div style={styles.decisionTime}>
                    <strong>📅 {decision.date}</strong>
                    <span style={styles.timeBadge}>🕒 {timeStr}</span>
                </div>
                <span style={styles.decisionId}>ID: {decision.id}</span>
            </div>

            <p style={styles.decisionText}>
                {expanded ? text : (isLong ? text.slice(0, 300) + '...' : text)}
            </p>

            {canExpand && (
                <button onClick={() => setExpanded(!expanded)} style={styles.readMoreBtn}>
                    {expanded ? 'Masquer les détails' : 'Voir le détail des décisions'}
                </button>
            )}

            {expanded && (
                <div style={styles.actionsPanel}>
                    {actions.length > 0 && (
                        <>
                            <div style={styles.actionsTitle}>Décisions de l'IA :</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 15 }}>
                                {actions.map((a, idx) => (
                                    <div key={idx} style={{ padding: '8px 12px', backgroundColor: 'var(--bg-box)', borderRadius: 8, borderLeft: `4px solid ${a.action === 'BUY' ? '#10b981' : '#ef4444'}`, border: '1px solid var(--border-color)', fontSize: 13 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span>
                                                <b style={{ color: a.action === 'BUY' ? '#059669' : '#dc2626' }}>{a.action}</b>
                                                <span style={{ fontWeight: 700, marginLeft: 8 }}>{a.quantity} {a.symbol}</span>
                                            </span>
                                        </div>
                                        <div style={{ color: '#64748b', fontStyle: 'italic', fontSize: 12 }}>{a.reason}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {decision.execution_result && (
                        <>
                            <div style={{ ...styles.actionsTitle, color: '#4ade80' }}>Rapport d'Exécution :</div>
                            <div style={{ ...styles.actionsContent, color: 'var(--text-main)', backgroundColor: 'rgba(74, 222, 128, 0.1)', border: '1px solid rgba(74, 222, 128, 0.3)', padding: 10, borderRadius: 8 }}>
                                {decision.execution_result.split('\n').map((line: string, i: number) => (
                                    <div key={i} style={{ marginBottom: 4 }}>• {line}</div>
                                ))}
                            </div>
                        </>
                    )}

                    {decision.fees > 0 && (
                        <div style={{ marginTop: 15, fontSize: 12, color: '#dc2626', fontWeight: 'bold', borderTop: '1px solid #bae6fd', paddingTop: 10 }}>
                            Total Frais de Transaction: -{decision.fees?.toLocaleString()} FCFA
                        </div>
                    )}
                </div>
            )}

            <TradingFeedback
                decisionId={decision.id}
                initialLikes={decision.likes_count || 0}
                initialHasLiked={decision.has_liked || false}
                initialCommentsCount={decision.comments_count || 0}
            />
        </div>
    );
};

interface DesktopNiaPortfolioProps {
    aiPortfolio: any;
    totalInvested: number;
    aiPositions: any[];
    aiDecisions: any[];
    portfolioHistory: any[];
    isAdmin: boolean;
    isPro: boolean;
    cycleLoading: boolean;
    setActiveModal: (modal: 'reset' | 'adjust' | 'run' | null) => void;
    setAdjustForm: (form: { amount: string, reason: string }) => void;
    handleCreateAiPortfolio: () => void;
    aiLoading: boolean;
}

const DesktopNiaPortfolio: React.FC<DesktopNiaPortfolioProps> = ({
    aiPortfolio,
    totalInvested,
    aiPositions,
    aiDecisions,
    portfolioHistory,
    isAdmin,
    isPro,
    cycleLoading,
    setActiveModal,
    setAdjustForm,
    handleCreateAiPortfolio,
    aiLoading
}) => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [decisions, setDecisions] = useState<any[]>(aiDecisions);
    const [decisionsTotal, setDecisionsTotal] = useState<number>(aiDecisions.length);
    const [decisionsLoading, setDecisionsLoading] = useState(false);

    // Sync when aiDecisions prop refreshes (after a cycle)
    React.useEffect(() => {
        setDecisions(aiDecisions);
    }, [aiDecisions]);

    const loadMoreDecisions = useCallback(async () => {
        if (!token || decisionsLoading) return;
        setDecisionsLoading(true);
        try {
            const res = await fetch(
                `${API_URL}/api/ai-portfolio/global/decisions?offset=${decisions.length}&limit=15`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!res.ok) return;
            const data = await res.json();
            setDecisions(prev => [...prev, ...data.decisions]);
            setDecisionsTotal(data.total);
        } catch (e) {
            console.error(e);
        } finally {
            setDecisionsLoading(false);
        }
    }, [token, decisions.length, decisionsLoading]);

    // On first meaningful load, fetch total count
    React.useEffect(() => {
        if (!token || !aiPortfolio) return;
        fetch(`${API_URL}/api/ai-portfolio/global/decisions?offset=0&limit=1`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()).then(d => setDecisionsTotal(d.total)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aiPortfolio]);
    const currentVal = aiPortfolio?.current_value || (aiPortfolio?.cash_available || 0) + (aiPortfolio?.cash_settling || 0) + aiPositions.reduce((acc, p) => acc + (p.current_value || 0), 0);
    const absolutePerf = aiPortfolio?.performance_abs ?? (currentVal - (totalInvested || 1000000));
    const percentPerf = aiPortfolio?.performance_pct ?? ((absolutePerf / (totalInvested || 1000000)) * 100);
    const startDate = aiPortfolio?.created_at ? new Date(aiPortfolio.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '16 février 2026';

    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
                padding: '16px 20px',
                background: 'var(--bg-box)',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                marginBottom: '20px',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start'
            }}>
                <span style={{ fontSize: '24px' }}>🤖</span>
                <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-heading)', marginBottom: '4px' }}>
                        Trading IA par Nia
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                        Cette page présente les activités de trading gérées par <strong>Nia</strong>.
                        Elle utilise une méthode basée sur des <strong>indicateurs de momentum</strong> (flux de niveau institutionnel, structure de continuation et tendance de fond) pour identifier les meilleures opportunités.
                        Ce portefeuille est présenté à <strong>but éducatif et démonstratif uniquement</strong>.
                    </div>
                </div>
            </div>

            <Box>
                <div style={{ padding: 20 }}>
                    {aiLoading ? (
                        <div style={{ textAlign: 'center', padding: 40 }}><div className="loading-spinner" /><p>Chargement...</p></div>
                    ) : !aiPortfolio ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <h3>🤖 Aucun portefeuille AI actif</h3>
                            <button onClick={handleCreateAiPortfolio} style={styles.primaryBtn}>Créer le Portefeuille de Nia</button>
                        </div>
                    ) : (
                        <>
                            <div style={styles.statsGrid}>
                                <div style={{ ...styles.statCard, backgroundColor: 'var(--bg-box)', border: '1px solid var(--border-color)' }}>
                                    <div style={styles.statLabel}>📅 Date de Démarrage</div>
                                    <div style={{ ...styles.statValue, color: 'var(--text-main)' }}>{startDate}</div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Lancement de la stratégie</div>
                                </div>
                                <div style={{ ...styles.statCard, backgroundColor: 'var(--bg-box)', border: '1px solid var(--border-color)' }}>
                                    <div style={styles.statLabel}>💰 Capital Investi Total</div>
                                    <div style={{ ...styles.statValue, color: 'var(--text-main)' }}>{(totalInvested || 1000000).toLocaleString()} FCFA</div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Démarrage + Apports</div>
                                </div>
                                <div style={{ ...styles.statCard, backgroundColor: 'var(--bg-box)', border: '1px solid var(--border-color)' }}>
                                    <div style={styles.statLabel}>🚀 Valeur Actuelle</div>
                                    <div style={{ ...styles.statValue, color: '#7c3aed' }}>{currentVal.toLocaleString()} FCFA</div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>Cash + Positions</div>
                                </div>
                                <div style={{ ...styles.statCard, backgroundColor: 'var(--bg-box)', borderBottom: `4px solid ${absolutePerf >= 0 ? '#10b981' : '#ef4444'}` }}>
                                    <div style={styles.statLabel}>📈 Performance Globale</div>
                                    <div style={{ ...styles.statValue, color: absolutePerf >= 0 ? '#10b981' : '#ef4444' }}>
                                        {absolutePerf >= 0 ? '+' : ''}{absolutePerf.toLocaleString()} FCFA
                                    </div>
                                    <div style={{ 
                                        fontSize: 14, 
                                        fontWeight: 800, 
                                        color: 'white', 
                                        backgroundColor: absolutePerf >= 0 ? '#10b981' : '#ef4444', 
                                        padding: '2px 10px', 
                                        borderRadius: 20, 
                                        display: 'inline-block',
                                        marginTop: 5
                                    }}>
                                        {absolutePerf >= 0 ? '▲' : '▼'} {percentPerf.toFixed(2)}%
                                    </div>
                                </div>
                                <div style={{ ...styles.statCard, backgroundColor: 'var(--bg-box)', borderBottom: '4px solid #ef4444' }}>
                                    <div style={styles.statLabel}>💸 Frais Cumulés</div>
                                    <div style={{ ...styles.statValue, color: '#ef4444' }}>
                                        -{aiPortfolio?.total_fees?.toLocaleString() || '0'} FCFA
                                    </div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                                        Déjà déduits de la valeur totale
                                    </div>
                                </div>
                                <div style={{ ...styles.statCard, backgroundColor: 'var(--bg-box)', border: '1px solid var(--border-color)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <div style={{ textAlign: 'left' }}>
                                            <div style={styles.statLabel}>💵 Cash</div>
                                            <div style={{ fontSize: 14, fontWeight: 'bold' }}>{aiPortfolio.cash_available?.toLocaleString()} FCFA</div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={styles.statLabel}>⏳ Règlement</div>
                                            <div style={{ fontSize: 14, fontWeight: 'bold', color: '#f59e0b' }}>{aiPortfolio.cash_settling?.toLocaleString()} FCFA</div>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8 }}>T+2 Status</div>
                                </div>
                            </div>

                            {isAdmin && (
                                <div style={styles.adminConsole}>
                                    <h4 style={{ ...styles.sectionTitle, marginTop: 0, color: '#991b1b' }}>🛡️ Console d'Administration</h4>
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                        <button onClick={() => setActiveModal('run')} disabled={cycleLoading} style={{ ...styles.adminBtn, backgroundColor: '#7c3aed' }}>⚡ Lancer Cycle AI</button>
                                        <button onClick={() => { setAdjustForm({ amount: '', reason: '' }); setActiveModal('adjust'); }} style={{ ...styles.adminBtn, backgroundColor: '#10b981' }}>💰 Créditer / Débiter</button>
                                        <button onClick={() => setActiveModal('reset')} style={{ ...styles.adminBtn, backgroundColor: '#ef4444' }}>⚠️ Réinitialiser</button>
                                    </div>
                                </div>
                            )}

                            {portfolioHistory.length >= 2 && (
                                <Box style={{ marginBottom: 30, padding: 20 }}>
                                    <NiaPortfolioHistory
                                        history={portfolioHistory}
                                        totalInvested={totalInvested}
                                    />
                                </Box>
                            )}

                            <h4 style={styles.sectionTitle}>📊 Positions Actuelles</h4>
                            <div style={styles.paywallContainer}>
                                <div style={{ overflowX: 'auto', marginBottom: 30 }}>
                                    <table style={styles.table}>
                                        <thead>
                                            <tr>
                                                <th style={styles.th}>Symbole</th>
                                                <th style={styles.thCenter}>Qté</th>
                                                <th style={styles.thCenter}>PRU</th>
                                                <th style={styles.thCenter}>Cours</th>
                                                <th style={styles.thCenter}>Valeur</th>
                                                <th style={styles.thCenter}>P&L</th>
                                                <th style={styles.thCenter}>Livré ?</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aiPositions.length === 0 ? (
                                                <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Aucune position.</td></tr>
                                            ) : aiPositions.map((p: any) => {
                                                const pnl = (p.current_value || 0) - (p.quantity * p.average_buy_price);
                                                const pnlPct = p.quantity > 0 ? ((p.current_value || 0) / (p.quantity * p.average_buy_price) - 1) * 100 : 0;
                                                const today = new Date(); today.setHours(0, 0, 0, 0);
                                                const isDelivered = !p.settlement_date || new Date(p.settlement_date) <= today;
                                                return (
                                                    <tr key={p.stock_symbol}>
                                                        <td style={styles.td}><b>{p.stock_symbol}</b></td>
                                                        <td style={styles.tdCenter}>{isPro ? p.quantity : '•••'}</td>
                                                        <td style={styles.tdCenter}>{isPro ? p.average_buy_price?.toLocaleString() : '••••'}</td>
                                                        <td style={styles.tdCenter}>{isPro ? (p.current_price?.toLocaleString() || '-') : '••••'}</td>
                                                        <td style={styles.tdCenter}>{isPro ? p.current_value?.toLocaleString() : '••••'}</td>
                                                        <td style={{ ...styles.tdCenter, color: pnl >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                                            {isPro ? `${pnl > 0 ? '+' : ''}${pnl?.toLocaleString()} (${pnlPct?.toFixed(1)}%)` : '••%'}
                                                        </td>
                                                        <td style={styles.tdCenter}>{isPro ? (isDelivered ? '✅ Oui' : `⏳ ${p.settlement_date}`) : '—'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {!isPro && (
                                    <div className="paywall-overlay">
                                        <div className="paywall-icon-circle">
                                            <i className="material-icons" style={{ fontSize: '24px', color: '#7c3aed' }}>lock</i>
                                        </div>
                                        <h4 className="paywall-title">
                                            Composition verrouillée
                                        </h4>
                                        <p className="paywall-desc">
                                            Consultez en temps réel les titres détenus, les quantités et les performances du portefeuille géré par le modèle de momentum.
                                        </p>
                                        <button onClick={() => navigate('/upgrade')} className="paywall-btn">
                                            Débloquer maintenant
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, marginTop: 10 }}>
                                <h4 style={{ ...styles.sectionTitle, marginBottom: 0, marginTop: 0 }}>🧠 Journal de Décisions</h4>
                                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                    {decisions.length} / {decisionsTotal} décisions
                                </span>
                            </div>
                            <div style={styles.paywallContainer}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {decisions.length === 0 ? (
                                        <p>Aucune décision.</p>
                                    ) : (
                                        (isPro ? decisions : decisions.slice(0, 3)).map(d => <DecisionCard key={d.id} decision={d} />)
                                    )}

                                    {isPro && decisions.length < decisionsTotal && (
                                        <button
                                            onClick={loadMoreDecisions}
                                            disabled={decisionsLoading}
                                            style={{
                                                marginTop: 8,
                                                padding: '12px 24px',
                                                borderRadius: 10,
                                                border: '1px solid var(--border-color)',
                                                background: 'var(--bg-input)',
                                                color: 'var(--text-main)',
                                                fontWeight: 600,
                                                fontSize: 14,
                                                cursor: decisionsLoading ? 'not-allowed' : 'pointer',
                                                opacity: decisionsLoading ? 0.6 : 1,
                                                alignSelf: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            {decisionsLoading
                                                ? '⏳ Chargement...'
                                                : `📜 Charger ${Math.min(15, decisionsTotal - decisions.length)} décisions de plus (${decisionsTotal - decisions.length} restantes)`
                                            }
                                        </button>
                                    )}
                                </div>

                                {!isPro && (
                                    <div className="paywall-overlay">
                                        <div className="paywall-icon-circle">
                                            <i className="material-icons" style={{ fontSize: '24px', color: '#7c3aed' }}>lock</i>
                                        </div>
                                        <h4 className="paywall-title">
                                            Journal de Décisions verrouillé
                                        </h4>
                                        <p className="paywall-desc">
                                            Accédez au journal des décisions d'achat et de vente de l'IA, avec les signaux techniques et le raisonnement motivant chaque opération.
                                        </p>
                                        <button onClick={() => navigate('/upgrade')} className="paywall-btn">
                                            Débloquer maintenant
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Box>
        </div>
    );
};

export default DesktopNiaPortfolio;
